import { Details, Flex, Confirmation, Toaster } from '@ui'
import React, { useState } from 'react'
import { Button, Intent, InputGroup } from '@blueprintjs/core'
import api, {
  FormErrors,
  BMDialogBuilding,
  IHRLocation,
  IBuildingType,
  CreateBuildingInput,
} from '@api'
import { omit, isFunction } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { rows } from './rows'
import { useAppData } from '@providers'
import { meridianEnabled } from './utils'
import { DateUtil } from '@utils'
// import moment from 'moment'

export type FormBuilding = Omit<
  BMDialogBuilding,
  'id' | 'floors' | 'geoLocation' | 'buildingType'
> & {
  geoLocation: IHRLocation | null
  buildingType: IBuildingType | null
}

interface BuildingFormProps {
  showIntegrations: boolean
  dirty: boolean
  building: FormBuilding
  setDirty: (dirty: boolean) => void
  onCancel: () => void
  onSubmit: (building: FormBuilding) => Promise<BMDialogBuilding>
}

export type BuildingFormErrors = FormErrors<FormBuilding>

interface RequiredField {
  key: 'name' | 'code' | 'address' | 'geoLocation'
  label: string
  error?: string
  validate?: <T extends FormBuilding[keyof FormBuilding]>(value: T) => boolean
}

const requiredFields: RequiredField[] = [
  { key: 'name', label: 'Name' },
  {
    key: 'address',
    label: 'Address',
  },
  {
    key: 'geoLocation',
    label: 'HR Location',
  },
  {
    key: 'code',
    label: 'Code',
    error: 'Code should contain at least 1 letter or number',
    validate(value) {
      const val = (value as string) || ''
      return /(?=.*\d)|(?=.*[a-z])|(?=.*[A-Z])/g.test(val)
    },
  },
]

export function BuildingForm({
  showIntegrations,
  dirty,
  setDirty,
  building: initState,
  onCancel,
  onSubmit,
}: BuildingFormProps) {
  const [building, setBuilding] = useState<FormBuilding>(initState)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<BuildingFormErrors>({})

  const update = (field: string, val: any) => {
    setDirty && setDirty(true)
    setErrors(omit(errors, field))
    setBuilding((prevState) => ({ ...prevState, [field]: val }))
  }

  function isValid(building: FormBuilding) {
    let valid = true
    requiredFields.forEach(({ key, validate }) => {
      if (valid) {
        valid = !!building[key]
      }
      valid = valid && isFunction(validate) ? validate?.(building[key]) : valid
    })

    return valid
  }

  const handleErrors = (error: ApolloError) => {
    setLoading(false)

    if (error.graphQLErrors && error.graphQLErrors.length) {
      const newErrors: BuildingFormErrors = error.graphQLErrors.reduce(
        (result: BuildingFormErrors, { invalidArguments }: any) => {
          if (invalidArguments) {
            return Object.assign(
              result,
              Object.entries(invalidArguments).reduce(
                (result, [key, value]) => ({
                  ...result,
                  [key]: value,
                }),
                {}
              )
            )
          }
          return result
        },
        {}
      )

      setErrors(newErrors)
    }
    Toaster.fail({ error })
  }

  const highlightErrors = () => {
    const foundErrors: BuildingFormErrors = {}
    requiredFields.forEach((field) => {
      if (!building[field.key]) {
        foundErrors[field.key] = `${field.label} is required`
      }

      if (
        !foundErrors[field.key] &&
        !(isFunction(field.validate) && field.validate(building[field.key]))
      ) {
        foundErrors[field.key] = field.error
      }
    })

    if (Object.keys(foundErrors).length) {
      setErrors((prevState) => ({ ...prevState, ...foundErrors }))
    }
  }

  const submit = () => {
    if (!isValid(building)) {
      highlightErrors()
      return
    }

    setLoading(true)
    onSubmit({
      ...building,
      meridianLocation: showIntegrations
        ? { externalId: building.meridianLocation?.externalId || '' }
        : null,
    }).catch(handleErrors)
  }

  return (
    <div style={{ paddingTop: 20 }}>
      <Details
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        <>
          {rows.map(({ label, required, formEl }) => (
            <Details.Row key={label}>
              <Details.Label {...{ label, required }} />
              {formEl(building, update, errors)}
            </Details.Row>
          ))}
        </>
        {showIntegrations && (
          <>
            <Details.Header iconName="puzzle-piece" label="Integrations" />
            <Details.Row key="meridian">
              <Details.Label label="Meridian Location External ID" />
              <InputGroup
                value={building.meridianLocation?.externalId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  update('meridianLocation', { externalId: e.target.value })
                }
              />
            </Details.Row>
          </>
        )}
        <Details.Footer>
          <Flex align="center" justify="flex-end">
            <div style={{ marginRight: 8 }}>
              <Confirmation enabled={dirty} text="Discard unsaved changes?" onConfirm={onCancel}>
                <Button
                  disabled={loading}
                  type="button"
                  text="Cancel"
                  onClick={() => {
                    if (!dirty) onCancel()
                  }}
                />
              </Confirmation>
            </div>
            <Button
              disabled={!dirty}
              intent={Intent.PRIMARY}
              loading={loading}
              text={'id' in building ? 'Update' : 'Create'}
              type="submit"
              data-button={'id' in building ? 'update_building' : 'create_building'}
            />
          </Flex>
        </Details.Footer>
      </Details>
    </div>
  )
}

export function NewBuildingForm({
  dirty,
  setDirty,
  onCancel,
  onSuccess,
}: {
  dirty: boolean
  setDirty: React.Dispatch<React.SetStateAction<boolean>>
  onCancel: () => void
  onSuccess: (building: any) => any
}) {
  const { integrations } = useAppData()
  const showIntegrations = integrations && meridianEnabled(integrations?.active)

  const newBuilding: FormBuilding = {
    name: '',
    address: '',
    code: '',
    closedAt: null,
    geoLocation: null,
    buildingType: null,
    meridianLocation: null,
  }

  return (
    <BuildingForm
      showIntegrations={showIntegrations}
      dirty={dirty}
      setDirty={setDirty}
      building={newBuilding}
      onCancel={onCancel}
      onSubmit={(building) => {
        const input: CreateBuildingInput = {
          name: building.name,
          code: building.code,
          address: building.address,
          geoLocationId: (building.geoLocation as IHRLocation).id,
          buildingTypeId: building.buildingType?.id || null,
          meridianLocation: building.meridianLocation,
          closedAt: building.closedAt ? DateUtil(building.closedAt).format('yyyy-MM-dd') : null,
        }

        return api.building.create(input).then((building: any) => onSuccess(building))
      }}
    />
  )
}
